import { useEffect, useMemo, useRef, useState } from 'react';
import { IComment, IERPUser } from '@admin-panel/types';
import { useCreate, useGetIdentity, useMany } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Avatar, Button, Flex, Form, Input, Typography } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { swallowEvent } from '@admin-panel/utils';

dayjs.extend(relativeTime);

const PAGE_SIZE = 99999;

interface ICommentsListProps {
  entityName: 'order' | 'package' | 'orderlineitem' | 'orders.return';
  entityId: string;
}

export const CommentsList = ({ entityId, entityName }: ICommentsListProps) => {
  const [comments, setComments] = useState<IComment[]>([]);
  const { data: identity } = useGetIdentity<IERPUser>();
  const currentUserId = identity?.id;

  const { tableProps } = useTable<IComment>({
    syncWithLocation: false,
    resource: 'comments',
    pagination: {
      pageSize: PAGE_SIZE,
      mode: 'server',
    },
    sorters: {
      permanent: [{ field: 'createdOn', order: 'desc' }],
    },
    filters: {
      permanent: [
        { field: 'entityName', operator: 'eq', value: entityName },
        { field: 'entityId', operator: 'eq', value: entityId },
      ],
    },
  });

  const pagination: any = tableProps.pagination === false ? undefined : tableProps.pagination;

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevScrollHeight = useRef<number | null>(null);

  useEffect(() => {
    if (!tableProps.dataSource) return;
    const current = pagination?.current || 1;
    if (current === 1) {
      setComments(tableProps.dataSource.slice().reverse());
    } else {
      const newComments = tableProps.dataSource
        .filter(newComment => !comments.some(existing => existing.id === newComment.id))
        .slice()
        .reverse();

      setComments(prev => [...newComments, ...prev]);

      if (scrollRef.current && prevScrollHeight.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevScrollHeight.current;
      }
    }
  }, [tableProps.dataSource]);

  const userIds = useMemo(
    () =>
      comments
        .map(comment => parseInt(comment?.createdBy))
        .filter((value, index, self) => self.indexOf(value) === index && Boolean(value)),
    [comments],
  );

  const {
    result: erpUsersResp,
    query: { isLoading: userFetchInProgress },
  } = useMany<IERPUser>({
    resource: 'erpusers',
    ids: userIds,
    queryOptions: {
      enabled: userIds.length > 0,
    },
  });

  const getUserDetails = (userId: string): IERPUser | null => {
    if (userFetchInProgress) return null;
    const user = erpUsersResp?.data?.find(user => user.id === parseInt(userId));
    if (user) return user;
    return null;
  };

  const observer = useRef<IntersectionObserver | null>(null);
  const firstItemRef = (node: HTMLElement | null) => {
    if (tableProps.loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      const current = pagination?.current || 1;
      const pageSize = pagination?.pageSize || PAGE_SIZE;
      const hasItems = (tableProps.dataSource?.length || 0) > 0;
      if (entries[0].isIntersecting && hasItems && !tableProps.loading) {
        prevScrollHeight.current = scrollRef.current?.scrollHeight || 0;
        pagination?.onChange?.(current + 1, pageSize);
      }
    });

    if (node) observer.current.observe(node);
  };

  useEffect(() => {
    const current = pagination?.current || 1;
    if (current === 1 && scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [comments, tableProps.pagination]);

  const { mutate: createComment } = useCreate<IComment>();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');

  const handleSend = () => {
    const value = draft.trim();
    if (!value) return;
    setCreating(true);
    createComment(
      {
        resource: 'comments',
        values: {
          comment: value,
          entityId: entityId,
          entityName: entityName,
        },
        successNotification: false,
      },
      {
        onSuccess: res => {
          const created = (res as any)?.data as IComment;
          if (created) {
            setComments(prev => [...prev, created]);
            setTimeout(() => {
              if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
              }
            }, 50);
          }
          setDraft('');
        },
        onSettled: () => setCreating(false),
      },
    );
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      swallowEvent(e);
      handleSend();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {tableProps.loading && (pagination?.current || 1) > 1 && (
          <div style={{ textAlign: 'center', padding: '10px' }}>Loading...</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {comments.map((item, index) => {
            const userDetails = getUserDetails(item.createdBy);
            const isCurrentUser = String(item.createdBy) === String(currentUserId);

            return (
              <div
                key={item.id}
                ref={index === 0 ? firstItemRef : null}
                style={{
                  display: 'flex',
                  flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <Avatar size={32} src={userDetails?.profileImageUrl} />
                <div
                  style={{
                    maxWidth: '70%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Typography.Text
                    style={{
                      fontSize: '11px',
                      color: '#666',
                      marginBottom: '2px',
                      textAlign: isCurrentUser ? 'right' : 'left',
                    }}
                  >
                    {userDetails?.name}
                  </Typography.Text>
                  <div
                    style={{
                      backgroundColor: '#fafafa',
                      color: '#000000',
                      padding: '8px 12px',
                      borderRadius: '12px',
                      borderTopLeftRadius: isCurrentUser ? '12px' : '4px',
                      borderTopRightRadius: isCurrentUser ? '4px' : '12px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography.Text
                      style={{
                        fontSize: '14px',
                        color: '#000000',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {item.comment}
                    </Typography.Text>
                  </div>
                  <Typography.Text
                    style={{
                      fontSize: '10px',
                      color: '#999',
                      marginTop: '2px',
                      textAlign: isCurrentUser ? 'right' : 'left',
                    }}
                  >
                    {dayjs(item.createdOn).fromNow()}
                  </Typography.Text>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        style={{
          padding: '12px 16px',
          borderTop: '1px solid #f0f0f0',
          background: '#fff',
        }}
      >
        <Form onFinish={handleSend}>
          <Flex gap={8} align="flex-end">
            <Form.Item style={{ flex: 1, marginBottom: 0 }}>
              <Input.TextArea
                value={draft}
                onChange={e => {
                  setDraft(e.target.value);
                }}
                onKeyDown={onKeyDown}
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="Write a comment..."
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                onClick={handleSend}
                loading={creating}
                disabled={!draft.trim()}
              >
                Send
              </Button>
            </Form.Item>
          </Flex>
        </Form>
      </div>
    </div>
  );
};
